import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import FieldSet from 'flarum/common/components/FieldSet';
import Switch from 'flarum/common/components/Switch';
import ItemList from 'flarum/common/utils/ItemList';
import listItems from 'flarum/common/helpers/listItems';
import extractText from 'flarum/common/utils/extractText';
import NotificationGrid from 'flarum/forum/components/NotificationGrid';
import PauseSettings from './PauseSettings';
import SleepSettings from './SleepSettings';
import { PREF, readPref, savePrefs } from '../utils/preferences';

const t = (key) => app.translator.trans(`happydigital-notification-settings.forum.${key}`);

/**
 * One place for everything notification related: pause, snooze, sleep mode
 * and the per-type grid (moved here from the core settings page).
 *
 * Reuses the core `SettingsPage` markup classes so fieldsets, switches and
 * the grid look exactly like the rest of the forum.
 */
export default class NotificationSettingsPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);

    if (!app.session.user) {
      m.route.set(app.route('index'));
      return;
    }

    this.muteAlertsLoading = false;

    app.history.push('happyNotifySettings', extractText(t('page.title')));
    app.setTitle(extractText(t('page.title')));
  }

  view() {
    const user = app.session.user;
    if (!user) return <div />;

    return (
      <div className="NotificationSettingsPage">
        <div className="container">
          <h2 className="App-titleControl App-titleControl--text NotificationSettingsPage-title">{t('page.title')}</h2>
          <div className="SettingsPage">
            <ul>{listItems(this.sectionItems(user).toArray())}</ul>
          </div>
        </div>
      </div>
    );
  }

  sectionItems(user) {
    const items = new ItemList();

    if (user.attribute('happyNotifyIsQuiet')) {
      items.add('quietNow', <p className="helpText">{t('quiet.active_now')}</p>, 110);
    }

    items.add(
      'pause',
      <FieldSet className="HappyNotify-pause" label={t('pause.heading')}>
        <PauseSettings user={user} />
      </FieldSet>,
      100
    );

    items.add(
      'sleep',
      <FieldSet className="HappyNotify-sleep" label={t('sleep.heading')}>
        <SleepSettings user={user} />
        <div>
          <div className="Form-group">
            <Switch
              state={!!readPref(user, PREF.muteAlerts, false)}
              loading={this.muteAlertsLoading}
              onchange={(value) => this.saveMuteAlerts(user, value)}
            >
              {t('quiet.mute_alerts_label')}
            </Switch>
            <p className="helpText">{t('quiet.mute_alerts_help')}</p>
          </div>
        </div>
      </FieldSet>,
      90
    );

    items.add(
      'types',
      <FieldSet className="Settings-notifications HappyNotify-types" label={t('types.heading')}>
        <p className="helpText">{t('types.help')}</p>
        <NotificationGrid user={user} />
      </FieldSet>,
      80
    );

    return items;
  }

  saveMuteAlerts(user, value) {
    this.muteAlertsLoading = true;

    savePrefs(user, { [PREF.muteAlerts]: value }).finally(() => {
      this.muteAlertsLoading = false;
      m.redraw();
    });
  }
}
